import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Loader from '../../common/Loader';
import MssModal from '../../common/MssModal';
import PageBreadcrumbs from '../../common/Breadcrumbs/PageBreadcrumbs';
import BreadcrumbCrumb from '../../common/Breadcrumbs/BreadcrumbCrumb';
import { useIsNarrowScreen } from '../../common/useIsNarrowScreen';
import useResourceDetail from '../../common/useResourceDetail';
import useUnsavedChangesGuard from '../../common/useUnsavedChangesGuard';
import AIResponseError from '../../common/AIResponseError';
import { TruncatedTitle } from '../../common/TruncatedTitle';
import {
  QUIZ_INSTRUCTIONS,
  QUIZ_SUBMIT_CONFIRM,
  QUIZ_UNANSWERED_MESSAGE,
  RESOURCE_LABEL,
  RESOURCE_STATUS,
} from '../../common/constants';
import { QuizGenerationStatus } from '../QuizGenerationStatus';
import ResourceInfoPopover from '../../common/ResourceInfoPopover';
import { QuestionListSheet } from '../QuestionListSheet';
import { QuizMobileBar } from '../QuizMobileBar';
import { QuestionCard } from './QuestionCard';
import { QuestionChecklist } from './QuestionChecklist';
import { QuizScore } from './QuizScore';
import { QuizResultsList } from './QuizResultsList';
import {
  RESULT_FILTERS,
  ResultsFilterTabs,
  isResultsFilterable,
  matchesFilter,
} from './ResultsFilterTabs';
import { gradeQuiz } from '../helpers';
import { deleteData, getData, postData } from '../../../DataAccessLayer';
import { quizSubmission, quizzes } from '../../../DataAccessLayer/services';

const SUBMIT_PROMPT = { UNANSWERED: 'unanswered', CONFIRM: 'confirm' };

function QuizPage() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  // The dashboard hands the name over when it sends you here, so the breadcrumb
  // can read properly before the quiz itself has arrived.
  const knownQuizTitle = useLocation().state?.title;
  const isImpersonating = useSelector((state) => state.impersonation.isImpersonating);
  const courseId = useSelector((state) => state.course.courseId);
  const showBanner = isImpersonating || !!courseId;

  /*
   * The shared loader rather than a fetch of our own: it drops a response that
   * arrives after the learner has moved to another quiz, and it turns a failed
   * load into something the page can show instead of an endless skeleton.
   */
  const {
    resource: quiz,
    setResource: setQuiz,
    isLoading,
    loadError,
  } = useResourceDetail({
    fetch: () => getData(quizzes({ id: quizId, courseId })),
    resourceLabel: 'practice quiz',
    id: quizId,
  });

  const [responses, setResponses] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  /*
   * Which submit modal is open, if either: the one asking the learner to finish
   * the quiz, or the one asking whether they are ready to hand it in.
   */
  const [submitPrompt, setSubmitPrompt] = useState(null);
  const [isQuestionListOpen, setIsQuestionListOpen] = useState(false);
  /*
   * A phone opens the results on the score and a list of the questions, and
   * reads a question picked from that list on its own. Both are the results
   * page; these two say which of them is on screen and how the list is filtered.
   */
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [resultFilter, setResultFilter] = useState(RESULT_FILTERS[0].value);
  const [checklistOpen, setChecklistOpen] = useState(null);
  const questionRefs = useRef([]);
  const isNarrow = useIsNarrowScreen();

  const applyQuiz = setQuiz;

  /*
   * A stored submission is the source of truth for which answers to show, so
   * the answers are seeded from it whenever a quiz arrives — on the first load
   * and again after submitting or clearing an attempt. Answering does not touch
   * the quiz itself, so this cannot wipe an attempt in progress.
   */
  useEffect(() => {
    setResponses(quiz?.submission?.responses ?? {});
  }, [quiz]);

  const isGraded = !!quiz?.submission;

  /*
   * The rail starts open on the results page, where the marks are part of
   * reading the score, and closed while taking so the questions get the width.
   * Null means the learner has not chosen for themselves yet.
   */
  const isChecklistOpen = checklistOpen ?? isGraded;
  const questions = useMemo(() => quiz?.questions ?? [], [quiz]);

  const { results, total, correctCount, percentage } = useMemo(
    () => gradeQuiz(questions, responses),
    [questions, responses],
  );
  const answeredCount = results.filter((result) => result.isAnswered).length;

  /*
   * An attempt lives in this page until it is handed in — nothing is written
   * until Submit — so leaving mid-quiz throws the answers away. A marked quiz
   * has nothing left to lose, so the guard lets go once it is submitted.
   */
  const { isPromptOpen, guardNavigation, stay, leave } = useUnsavedChangesGuard({
    isDirty: !isGraded && answeredCount > 0,
    backPath: '../..',
  });

  /*
   * A phone shows one question at a time, taking the quiz and reading the marked
   * one back alike, with the bar at the foot of the screen paging through them.
   * Submit therefore only appears once the learner reaches the last question;
   * wider screens list every question and always show Submit under them.
   */
  const isOneQuestionAtATime = isNarrow;
  const isSubmitVisible = !isOneQuestionAtATime || activeIndex === total - 1;

  // The two halves of the results page on a phone; neither on a wider screen,
  // which has room to show the score and every question at once.
  const isResultsList = isNarrow && isGraded && !isResultOpen;
  const isResultDetail = isNarrow && isGraded && isResultOpen;

  /*
   * A quiz answered all one way has nothing to tell apart, so the tabs are not
   * offered and the filter falls back to the whole quiz — otherwise "Incorrect"
   * on a perfect score would offer to hide every question.
   */
  const isFilterable = isGraded && isResultsFilterable(results);
  const activeFilter = isFilterable ? resultFilter : RESULT_FILTERS[0].value;

  /*
   * A phone shows one question at a time; a wide screen shows the whole quiz,
   * narrowed to the tab in front of the learner once it has been marked.
   */
  const visibleResults = results
    .map((result, index) => ({ result, index }))
    .filter(({ result, index }) =>
      isOneQuestionAtATime ? index === activeIndex : matchesFilter(result, activeFilter),
    );

  /*
   * The rail follows the tabs. Every question keeps its place in it, so the
   * numbering stays the quiz's own, but the ones the tab leaves out have
   * nothing to jump to and are shown as unavailable.
   */
  const enabledIndexes = useMemo(() => {
    const enabled = new Set();
    results.forEach((result, index) => {
      if (matchesFilter(result, activeFilter)) enabled.add(index);
    });
    return enabled;
  }, [results, activeFilter]);

  // The page itself scrolls, so switching views returns to the top of it.
  const scrollQuestionsToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleSelectOption = (questionId, optionKey) => {
    setResponses((prev) => ({ ...prev, [questionId]: optionKey }));
    setActiveIndex(questions.findIndex((question) => question.id === questionId));
  };

  const handleSelectQuestion = (index) => {
    setActiveIndex(index);
    // Only one question is on screen at a time on a phone, so there is nothing
    // to scroll to — the card is replaced and the page returns to the top.
    const card = questionRefs.current[index];
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      scrollQuestionsToTop();
    }
  };

  /*
   * Submitting is confirmed rather than immediate, and a quiz with questions
   * still unanswered is turned back at the same point — the button stays live
   * either way, so the learner is told what is missing instead of guessing.
   */
  const handleSubmitClick = () =>
    setSubmitPrompt(answeredCount < total ? SUBMIT_PROMPT.UNANSWERED : SUBMIT_PROMPT.CONFIRM);

  const handleSubmit = async () => {
    setSubmitPrompt(null);
    setIsSaving(true);
    try {
      // The score travels with the responses so the backend can keep the best attempt.
      applyQuiz(
        await postData(quizSubmission(quizId, courseId), {
          responses,
          score: { correctCount, total, percentage },
        }),
      );
      // The marked quiz is read back from the start: the list of questions on a
      // phone, and the first of them rather than the last one answered.
      setActiveIndex(0);
      setIsResultOpen(false);
      scrollQuestionsToTop();
    } catch (err) {
      console.log(err);
      toast.error('Failed to submit quiz. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const startFresh = () => {
    setResponses({});
    setActiveIndex(0);
    setIsResultOpen(false);
    scrollQuestionsToTop();
  };

  const handleRetake = async () => {
    setIsSaving(true);
    try {
      // Only the attempt in progress is cleared; the best attempt is kept.
      applyQuiz(await deleteData(quizSubmission(quizId, courseId)));
      startFresh();
    } catch (err) {
      console.log(err);
      toast.error('Failed to restart quiz. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // The dashboard, where the other study tools are — not the generator one
  // level up, which only makes another quiz.
  const handleStudyOther = () => navigate('../..', { relative: 'path' });

  // A question opened from the results list is read on its own, with the bar at
  // the foot of the screen paging on from it.
  const handleOpenResult = (index) => {
    setActiveIndex(index);
    setIsResultOpen(true);
    scrollQuestionsToTop();
  };

  const handleShowResultsList = () => {
    setIsResultOpen(false);
    scrollQuestionsToTop();
  };

  /*
   * The page's frame stands while the quiz is fetched, with a block standing in
   * for the panel — the same way a deck's pages load.
   */
  if (isLoading || loadError || !quiz) {
    return (
      <Box className={`mss__quizPage ${showBanner ? 'impersonating' : ''}`} id="quizPage">
        <PageBreadcrumbs id="quizPage__breadcrumbs" rootTo="../.." isVisible={!loadError}>
          <BreadcrumbCrumb isLoading={!knownQuizTitle}>
            <TruncatedTitle name={knownQuizTitle} variant="inherit" />
          </BreadcrumbCrumb>
        </PageBreadcrumbs>

        {loadError ? (
          <AIResponseError
            message={loadError}
            onGoToDashboard={() => navigate('../..', { relative: 'path' })}
          />
        ) : (
          <Skeleton
            id="quizPage__skeleton_loading"
            variant="rectangular"
            height={240}
            sx={{ mt: '1rem' }}
          />
        )}
      </Box>
    );
  }

  /*
   * What to do once the quiz is marked. The pair follows the questions on a wide
   * screen and sits in the header on a phone, so it is rendered with the class
   * the place it lands calls for: the footer sizes its own buttons, while in the
   * header they are left to look like every other header's actions do.
   */
  const resultsActions = (buttonClass = '') => (
    <>
      <Button
        variant="contained"
        className={`mss__button ${buttonClass}`.trim()}
        id="quizPage__button_footerStudyOther"
        onClick={handleStudyOther}
      >
        Study Other Materials
      </Button>
      <Button
        variant="contained"
        className={`mss__button primary ${buttonClass}`.trim()}
        id="quizPage__button_footerRetake"
        disabled={isSaving}
        onClick={handleRetake}
      >
        Try the Quiz Again
      </Button>
    </>
  );

  /*
   * A phone reads the results from a list that runs to the foot of the page, so
   * what to do next sits beside the heading there instead — there is no end of
   * the questions for it to follow.
   */
  const footerContent = isGraded
    ? !isNarrow && resultsActions('footerButton')
    : isSubmitVisible && (
        <Button
          variant="contained"
          className="mss__button primary footerButton submitButton"
          id="quizPage__button_submit"
          disabled={isSaving}
          onClick={handleSubmitClick}
        >
          Submit Practice Quiz
        </Button>
      );

  const questionList = (
    <Box className="questionList" id="quizPage__questionList">
      {visibleResults.map(({ result, index }) => (
        <QuestionCard
          key={result.question.id}
          id="quizPage"
          index={index}
          result={result}
          isGraded={isGraded}
          onSelect={handleSelectOption}
          cardRef={(node) => (questionRefs.current[index] = node)}
        />
      ))}
    </Box>
  );

  return (
    <Box className={`mss__quizPage ${showBanner ? 'impersonating' : ''}`} id="quizPage">
      <Loader id="quizPage" isOpen={isSaving} />

      {/* Nothing to confirm — the quiz is not finished, so it cannot be handed in. */}
      <MssModal
        id="quizPage__unansweredModal"
        isOpen={submitPrompt === SUBMIT_PROMPT.UNANSWERED}
        onClose={() => setSubmitPrompt(null)}
        title="Submit Practice Quiz"
        primaryButtonTitle="Go Back"
        onPrimaryClick={() => setSubmitPrompt(null)}
      >
        <Typography>{QUIZ_UNANSWERED_MESSAGE}</Typography>
      </MssModal>

      <MssModal
        id="quizPage__confirmSubmitModal"
        isOpen={submitPrompt === SUBMIT_PROMPT.CONFIRM}
        onClose={() => setSubmitPrompt(null)}
        title="Submit Practice Quiz"
        primaryButtonTitle="Submit Practice Quiz"
        onPrimaryClick={handleSubmit}
        isPrimaryButtonDisabled={isSaving}
        secondaryButtonTitle="Keep Working"
        onSecondaryClick={() => setSubmitPrompt(null)}
        maxWidth="sm"
      >
        <Typography>{QUIZ_SUBMIT_CONFIRM}</Typography>
      </MssModal>

      <MssModal
        id="quizPage__modal_unsavedAnswers"
        isOpen={isPromptOpen}
        onClose={stay}
        title="Unsaved Changes"
        primaryButtonTitle="Stay"
        onPrimaryClick={stay}
        secondaryButtonTitle="Leave"
        onSecondaryClick={leave}
      >
        <Typography>Answers you have not submitted will be lost. Do you wish to leave?</Typography>
      </MssModal>

      <PageBreadcrumbs
        id="quizPage__breadcrumbs"
        rootTo="../.."
        onRootClick={(event) => {
          event.preventDefault();
          guardNavigation('../..');
        }}
      >
        <BreadcrumbCrumb>
          <TruncatedTitle name={quiz.title} variant="inherit" />
        </BreadcrumbCrumb>
      </PageBreadcrumbs>

      {/*
        Outside the panel, as a deck's name is: the name belongs to the page,
        not to the questions in it. .resourceHeader is the same header the
        flashcard pages use, so the two read alike.
      */}
      {quiz.status === RESOURCE_STATUS.COMPLETED && (
        <Box className="resourceHeader" id="quizPage__box_header">
          <Box className="resourceTitleGroup">
            <Box className="resourceTitle">
              <TruncatedTitle
                id="quizPage__text_title"
                component="h1"
                name={quiz.title}
                suffix={isGraded ? ' Results' : ''}
              />
              <ResourceInfoPopover
                id="quizPage"
                name={quiz.title}
                nameLabel={RESOURCE_LABEL.QUIZ}
                resources={quiz.resources}
                lastEditedBy={quiz.lastEditedBy}
                // A quiz being taken says how to take it; a marked one has
                // nothing left to explain.
                instructions={isGraded ? undefined : QUIZ_INSTRUCTIONS}
              />
            </Box>
            <Typography className="resourceCount">
              <strong>{total}</strong> questions
            </Typography>
          </Box>

          {isResultsList && (
            <Box className="resourceActions resourceActions--stack" id="quizPage__resultsActions">
              {resultsActions()}
            </Box>
          )}
        </Box>
      )}

      <Box className="mss__box quizContent" id="quizPage__box">
        <QuizGenerationStatus
          quizId={quizId}
          courseId={courseId}
          status={quiz.status}
          errorCode={quiz.errorCode}
          onGenerated={applyQuiz}
          onGenerateNew={() => navigate('..', { relative: 'path' })}
          onGoToDashboard={() => navigate('../..', { relative: 'path' })}
        />

        {quiz.status === RESOURCE_STATUS.COMPLETED && (
          <>
            {/*
              The rail and the rest of the page share a row, so everything beside
              it shifts as it expands and collapses.
            */}
            <Box className={`quizBody ${isGraded ? 'graded' : ''}`} id="quizPage__body">
              {!isNarrow && (
                <QuestionChecklist
                  id="quizPage"
                  results={results}
                  isGraded={isGraded}
                  activeIndex={activeIndex}
                  isOpen={isChecklistOpen}
                  enabledIndexes={enabledIndexes}
                  onToggle={setChecklistOpen}
                  onSelect={handleSelectQuestion}
                />
              )}

              {/*
                The score is the head of the list a phone opens the results on,
                not of a single question read from it.
              */}
              {isGraded && !isResultDetail && (
                <QuizScore
                  id="quizPage"
                  correctCount={correctCount}
                  total={total}
                  percentage={percentage}
                  bestScore={quiz.bestScore}
                  isNewHighScore={Boolean(quiz.submission?.isNewHighScore)}
                />
              )}

              {isResultsList ? (
                <QuizResultsList
                  id="quizPage"
                  results={results}
                  filter={activeFilter}
                  isFilterable={isFilterable}
                  onFilterChange={setResultFilter}
                  onSelect={handleOpenResult}
                />
              ) : (
                <Box className="quizQuestions" id="quizPage__questions">
                  {/* A wide screen reads the marked cards themselves, so the
                      tabs that narrow them sit directly above. */}
                  {isFilterable && !isNarrow && (
                    <ResultsFilterTabs
                      id="quizPage"
                      filter={activeFilter}
                      onChange={setResultFilter}
                    />
                  )}
                  {questionList}
                </Box>
              )}

              {total > 0 && footerContent && (
                <Box className={`quizFooter ${isGraded ? 'graded' : ''}`} id="quizPage__footer">
                  {footerContent}
                </Box>
              )}
            </Box>

            {isNarrow && (
              <QuestionListSheet
                id="quizPage"
                isOpen={isQuestionListOpen}
                onClose={() => setIsQuestionListOpen(false)}
                questions={questions.map((question) => question.question)}
                results={results}
                isGraded={isGraded}
                activeIndex={activeIndex}
                onSelect={handleSelectQuestion}
                // The way back to the score, which the results page leaves for a
                // question opened from its list.
                onBack={isGraded ? handleShowResultsList : undefined}
              />
            )}
          </>
        )}
      </Box>

      {/*
        Outside the panel, so it pins itself to the foot of the screen rather
        than to the end of the questions.
      */}
      {/* The list is its own way through the questions and needs no paging. */}
      {isNarrow && !isResultsList && quiz.status === RESOURCE_STATUS.COMPLETED && total > 0 && (
        <QuizMobileBar
          id="quizPage"
          index={activeIndex}
          total={total}
          onChange={handleSelectQuestion}
          onOpenList={() => setIsQuestionListOpen(true)}
        />
      )}
    </Box>
  );
}

export default QuizPage;
